import React, { useState } from "react";
import { Form, FormGroup, Input, Label } from "reactstrap";
import { Btn } from "../AbstractElements";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { storeDispatch } from "../store";
import { login } from "../store/auth";
import { authService } from "../services/authService";
import { Shield, Zap, Users } from "react-feather";

const FEATURES = [
  {
    icon: <Shield size={17} />,
    title: "Secure Access Control",
    desc: "Manage users, roles, and partner licenses with confidence.",
  },
  {
    icon: <Zap size={17} />,
    title: "Real-time Device Control",
    desc: "Monitor and control building systems instantly.",
  },
  {
    icon: <Users size={17} />,
    title: "Partner Management",
    desc: "Oversee properties, tenants, and permissions in one place.",
  },
];

const Signin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [togglePassword, setTogglePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const history = useNavigate();

  const loginAuth = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.signIn({ email, password });

      const allowedUserTypes = ["ADMIN", "MANAGER"];
      if (!allowedUserTypes.includes(response?.userType)) {
        toast.error("Access denied. Only Admin and Manager users can access this panel.");
        return;
      }

      if (!response?.canAccessAdminPanel) {
        toast.error("Access denied. You do not have permission to access the admin panel.");
        return;
      }

      if (!response?.verified) {
        toast.info("Please verify your email to continue.");
        history(`/verify-otp`, { state: { userData: response } });
        return;
      }

      storeDispatch(login({ user: response }));
      toast.success("Successfully logged in!");
      setTimeout(() => history(`/dashboard`), 500);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Invalid email or password");
      } else if (error.response?.status === 403) {
        const code = error.response?.data?.errorCode || error.response?.data?.code || error.response?.data?.error;
        if (code === "LICENSE_EXPIRED" || code === "LICENSE_INVALID") {
          toast.error(error.response?.data?.message || "Your organization's license has expired. Please contact your administrator.");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-split">

        {/* ── Left: Brand panel ── */}
        <div className="login-brand-panel">
          <div className="login-brand-inner">

            <div className="login-brand-logo">
              <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Manzili Smart" style={{ height: 120, maxWidth: '100%' }} />
            </div>

            <p className="login-brand-tagline">
              Professional-grade building intelligence for residents and managers.
            </p>

            <ul className="login-feature-list">
              {FEATURES.map((f, i) => (
                <li key={i} className="login-feature-item">
                  <span className="login-feature-icon">{f.icon}</span>
                  <div>
                    <strong>{f.title}</strong>
                    <span>{f.desc}</span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="login-brand-footer">
              <span className="login-powered-label">Powered by</span>
              <span className="login-powered-value">Siemens Desigo Optic</span>
            </div>

          </div>
        </div>

        {/* ── Right: Form panel ── */}
        <div className="login-form-panel">
          <div className="login-form-inner">

            <div className="login-form-header">
              <div className="login-mobile-logo">
                <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Manzili Smart" style={{ height: 120, maxWidth: '100%' }} />
              </div>
              <h2>Welcome back</h2>
              <p>Sign in to your management dashboard</p>
            </div>

            <Form className="theme-form" onSubmit={loginAuth}>
              <FormGroup>
                <Label className="col-form-label" htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  className="form-control"
                  type="email"
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  disabled={isLoading}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
              </FormGroup>

              <FormGroup className="position-relative">
                <Label className="col-form-label" htmlFor="password">Password</Label>
                <div className="position-relative">
                  <Input
                    id="password"
                    className="form-control"
                    type={togglePassword ? "text" : "password"}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') loginAuth(e); }}
                    value={password}
                    disabled={isLoading}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                  />
                  <div className="show-hide" onClick={() => setTogglePassword(!togglePassword)}>
                    <span className={togglePassword ? "" : "show"}></span>
                  </div>
                </div>
              </FormGroup>

              <FormGroup className="mb-0">
                <Btn
                  attrBtn={{
                    color: "primary",
                    className: "d-block w-100 mt-2",
                    type: "submit",
                    disabled: isLoading,
                  }}
                >
                  {isLoading ? "Signing In…" : "Sign In"}
                </Btn>
              </FormGroup>

              <p className="login-consent">
                By signing in, you agree to our{' '}
                <Link to="/terms">Terms of Use</Link>
                {' '}and{' '}
                <Link to="/privacy">Privacy Policy</Link>.
              </p>
            </Form>

          </div>
        </div>

      </div>
    </div>
  );
};

export default Signin;
